import { User, getAuth } from 'firebase/auth';
import { getApps } from 'firebase/app';
import { auth } from '../firebase';
import { getBackendUrl } from '../config';
import { SeasonRoster, UserProfile, CalendarEvent, VolunteerDuties, AdminUsersResponse, AccessRequest, UserActivityStats, GameVideoSummary, IngestionStatusResponse, GameTaskLogResponse, FaststartRepairResponse, UserNotificationPreferences, EmailDispatchesResponse, GamePeriod, AudioWaveformSummary, TimelineAuditEntry, GoalEventData, StoryboardMetadata, TimelineEventItem, TimelineShareTarget, GeneralAccessLevel, ShareAccessRole, UnifiedStandingsResponse, WeeklyMatchupsResponse } from '../types';

/**
 * Custom error class for API errors that includes HTTP status code.
 */
export class ApiError extends Error {
    status: number;
    constructor(message: string, status: number) {
        super(message);
        this.status = status;
        this.name = 'ApiError';
    }
}

let currentActiveUser: User | null = null;

/**
 * Sets the active user whose ID token will be attached to API requests.
 */
type TokenRefreshedListener = (token: string) => void;
const tokenListeners = new Set<TokenRefreshedListener>();

/**
 * Registers a listener callback invoked whenever the Firebase ID token is renewed or refreshed.
 */
export function onTokenRefreshed(listener: TokenRefreshedListener): () => void {
    tokenListeners.add(listener);
    return () => {
        tokenListeners.delete(listener);
    };
}

export function notifyTokenRefreshed(token: string) {
    tokenListeners.forEach((listener) => {
        try {
            listener(token);
        } catch (e) {
            console.error("Error in token refreshed listener:", e);
        }
    });
}

/**
 * Sets the active user whose ID token will be attached to API requests.
 */
export function setActiveUser(user: User | null) {
    currentActiveUser = user;
}

/**
 * Retrieves the current Firebase ID token, if a user is logged in.
 * Supports forceRefresh to obtain a freshly minted token from Firebase Auth.
 */
export async function getAuthToken(forceRefresh: boolean = false): Promise<string | null> {
    let user: User | null = currentActiveUser;

    // If currentActiveUser is missing or is a temporary frame-1 placeholder, search active Firebase instances
    if (!user || (user as any).isPlaceholder) {
        try {
            await auth.authStateReady();
        } catch (e) {}

        if (auth.currentUser) {
            user = auth.currentUser;
            currentActiveUser = user;
        } else {
            const apps = getApps() || [];
            for (const app of apps) {
                try {
                    const a = getAuth(app);
                    await a.authStateReady();
                    if (a.currentUser) {
                        user = a.currentUser;
                        currentActiveUser = user;
                        break;
                    }
                } catch (e) {}
            }
        }
    }

    if (!user || (user as any).isPlaceholder) return null;

    try {
        const token = await user.getIdToken(forceRefresh);
        if (!token && auth.currentUser) {
            return await auth.currentUser.getIdToken(forceRefresh);
        }
        return token || null;
    } catch (e) {
        console.error("Failed to get Firebase auth token:", e);
        return null;
    }
}

let tokenRefreshPromise: Promise<string | null> | null = null;

/**
 * Proactively or reactively forces a Firebase ID token refresh.
 * Concurrent callers share the same refresh promise to prevent duplicate requests.
 */
export async function refreshAuthToken(): Promise<string | null> {
    if (tokenRefreshPromise) {
        return tokenRefreshPromise;
    }

    tokenRefreshPromise = (async () => {
        try {
            const freshToken = await getAuthToken(true);
            if (freshToken) {
                notifyTokenRefreshed(freshToken);
            }
            return freshToken;
        } finally {
            tokenRefreshPromise = null;
        }
    })();

    return tokenRefreshPromise;
}

/**
 * Performs a fetch request with the Firebase ID token automatically attached as a Bearer token.
 * Automatically catches HTTP 401 Unauthorized errors, refreshes the expired token silently in the background,
 * and retries the request once without bubbling auth failure up to the user.
 */
export async function authenticatedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    let token = await getAuthToken();

    // If token is temporarily unavailable on cold start, retry briefly before giving up
    if (!token) {
        for (let attempt = 0; attempt < 5; attempt++) {
            await new Promise(r => setTimeout(r, 300));
            token = await getAuthToken();
            if (token) break;
        }
        if (!token && auth.currentUser) {
            try {
                token = await auth.currentUser.getIdToken(true);
            } catch (e) {}
        }
    }

    const headers = new Headers(init?.headers || {});

    if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
    }

    let response = await fetch(input, {
        ...init,
        headers,
    });

    // If request returned 401 Unauthorized, the ID token may have expired.
    // Force-refresh the token once and retry the request transparently.
    if (response.status === 401) {
        const freshToken = await refreshAuthToken();
        if (freshToken) {
            const retryHeaders = new Headers(init?.headers || {});
            retryHeaders.set('Authorization', `Bearer ${freshToken}`);
            response = await fetch(input, {
                ...init,
                headers: retryHeaders,
            });
        }
    }

    return response;
}

/**
 * Fetches the current user's profile and access permissions from the backend.
 */
export async function fetchUserProfile(): Promise<UserProfile> {
    const url = `${getBackendUrl()}/api/auth/me`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        throw new ApiError(`Failed to fetch user profile: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Fetches all season rosters from the backend Firestore database.
 */
export async function fetchRosters(forceRefresh: boolean = false): Promise<Record<string, SeasonRoster>> {
    const url = new URL(`${getBackendUrl()}/api/rosters`);
    if (forceRefresh) {
        url.searchParams.set('refresh', 'true');
    }
    const res = await authenticatedFetch(url.toString());
    if (!res.ok) {
        throw new ApiError(`Failed to fetch rosters: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Fetches the roster for a specific season.
 */
export async function fetchRoster(seasonKey: string): Promise<SeasonRoster> {
    const url = `${getBackendUrl()}/api/rosters/${encodeURIComponent(seasonKey)}`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        throw new ApiError(`Failed to fetch roster for ${seasonKey}: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Fetches volunteer duties from the backend.
 */
export async function fetchVolunteerDuties(): Promise<Record<string, VolunteerDuties>> {
    const url = `${getBackendUrl()}/api/volunteer-duties`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        throw new ApiError(`Failed to fetch volunteer duties: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Fetches calendar events from the backend.
 */
export async function fetchCalendarEvents(tags: string = '9564728'): Promise<CalendarEvent[]> {
    const url = `${getBackendUrl()}/api/calendar?tags=${encodeURIComponent(tags)}`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        throw new ApiError(`Failed to fetch calendar events: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Fetches all special users, roles, and roster users summary (Admin only).
 */
export async function fetchAdminUsers(): Promise<AdminUsersResponse> {
    const url = `${getBackendUrl()}/api/admin/users`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        throw new ApiError(`Failed to fetch admin users: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Adds or updates a special user and assigned roles (Admin only).
 */
export async function addAdminUser(userData: {
    email: string;
    name?: string;
    roles: string[];
    notes?: string;
}): Promise<any> {
    const url = `${getBackendUrl()}/api/admin/users`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new ApiError(data.error || `Failed to save user: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Updates roles and notes for a specific special user (Admin only).
 */
export async function updateAdminUser(email: string, userData: {
    name?: string;
    roles: string[];
    notes?: string;
}): Promise<any> {
    const url = `${getBackendUrl()}/api/admin/users/${encodeURIComponent(email)}`;
    const res = await authenticatedFetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new ApiError(data.error || `Failed to update user: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Removes a special user and revokes their special roles (Admin only).
 */
export async function deleteAdminUser(email: string): Promise<any> {
    const url = `${getBackendUrl()}/api/admin/users/${encodeURIComponent(email)}`;
    const res = await authenticatedFetch(url, {
        method: 'DELETE'
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new ApiError(data.error || `Failed to remove user: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Submits an access request for the currently signed-in user.
 */
export async function submitAccessRequest(requestData: {
    name: string;
    reason: string;
    email?: string;
}): Promise<{ message: string; request: AccessRequest }> {
    const url = `${getBackendUrl()}/api/access-requests`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new ApiError(data.error || `Failed to submit access request: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Fetches the access request status for the currently signed-in user.
 */
export async function fetchUserAccessRequestStatus(): Promise<{ request: AccessRequest | null }> {
    const url = `${getBackendUrl()}/api/access-requests/status`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new ApiError(data.error || `Failed to check access request status: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Fetches all access requests (Admin only).
 */
export async function fetchAdminAccessRequests(): Promise<AccessRequest[]> {
    const url = `${getBackendUrl()}/api/admin/access-requests`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new ApiError(data.error || `Failed to load access requests: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Approves an access request and assigns roles to the user (Admin only).
 */
export async function approveAdminAccessRequest(email: string, roles: string[] = ['viewer']): Promise<{ message: string; request: AccessRequest }> {
    const url = `${getBackendUrl()}/api/admin/access-requests/${encodeURIComponent(email)}/approve`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roles })
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new ApiError(data.error || `Failed to approve access request: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Rejects an access request (Admin only).
 */
export async function rejectAdminAccessRequest(email: string): Promise<{ message: string; request: AccessRequest }> {
    const url = `${getBackendUrl()}/api/admin/access-requests/${encodeURIComponent(email)}/reject`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new ApiError(data.error || `Failed to reject access request: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Pings backend to record user activity/visit.
 */
export async function pingUserActivity(path?: string): Promise<{ status: string; activity: UserActivityStats }> {
    const url = `${getBackendUrl()}/api/activity/ping`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: path || (typeof window !== 'undefined' ? window.location.pathname : '') })
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new ApiError(data.error || `Failed to ping user activity: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Fetches all game videos with highlights catalog.
 */
export async function fetchGameVideos(seasonKey?: string, status?: string): Promise<GameVideoSummary[]> {
    const params = new URLSearchParams();
    if (seasonKey) params.set('seasonKey', seasonKey);
    if (status) params.set('status', status);

    const query = params.toString() ? `?${params.toString()}` : '';
    const url = `${getBackendUrl()}/api/videos${query}`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to fetch game videos: ${res.statusText}`, res.status);
    }
    const data = await res.json();
    return Array.isArray(data) ? data : (data.videos || []);
}

/**
 * Fetches a single game video's detailed metadata, periods, and goals.
 */
export async function fetchGameVideoDetails(gameId: string): Promise<GameVideoSummary> {
    const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        throw new ApiError(`Failed to fetch game video details: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Triggers backend lossless cutting of custom clip and returns Blob for download.
 */
export async function downloadCustomClip(gameId: string, startSeconds: number, endSeconds: number, feed: string = 'auto', title?: string): Promise<Blob> {
    const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/clip?download=true`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ startSeconds, endSeconds, feed, feedType: feed, download: true, title })
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to create custom clip: ${res.statusText}`, res.status);
    }
    return res.blob();
}

/**
 * Triggers backend download of pre-generated highlight clip and returns Blob for download.
 */
export async function downloadHighlightClip(gameId: string, filename: string): Promise<Blob> {
  const cleanName = filename.includes('/') ? filename.substring(filename.lastIndexOf('/') + 1) : filename;
  const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/highlights/${encodeURIComponent(cleanName)}?download=true`;
  const res = await authenticatedFetch(url);
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new ApiError(errData.error || `Failed to download highlight clip: ${res.statusText}`, res.status);
  }
  return res.blob();
}

/**
 * Returns canonical backend relative URL for playing or viewing a highlight clip,
 * extracting filename from full legacy GCS URLs if necessary.
 */
export function getHighlightClipUrl(gameId: string, clipUrlOrFilename: string): string {
  if (!clipUrlOrFilename) return '';
  const withoutQuery = clipUrlOrFilename.split('?')[0];
  const filename = withoutQuery.includes('/')
    ? withoutQuery.substring(withoutQuery.lastIndexOf('/') + 1)
    : withoutQuery;
  return `/api/videos/${encodeURIComponent(gameId)}/highlights/${encodeURIComponent(filename)}`;
}

export interface VideoStreamInfo {
  url: string;
  feed?: string;
  variant?: 'master' | 'scrub';
  gameId?: string;
  filename?: string;
  expirationSeconds?: number;
  direct: boolean;
  download?: boolean;
}

/**
 * Resolves a streaming URL for a game video feed (auto or pano).
 * Calls backend endpoint with redirect=false to obtain a direct GCS V4 signed URL
 * valid for 24 hours (86400s), allowing continuous playback without token expiry interruptions.
 * Pass variant='scrub' to obtain the ultra-dense keyframe 360p proxy for laser-fast seeking.
 */
export async function fetchVideoStreamUrl(
  gameId: string,
  feed: 'auto' | 'pano' = 'auto',
  variant: 'master' | 'scrub' = 'master'
): Promise<VideoStreamInfo> {
  const variantParam = variant === 'scrub' ? '&variant=scrub' : '';
  const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/stream?feed=${encodeURIComponent(feed)}${variantParam}&redirect=false`;
  const res = await authenticatedFetch(url, {
    headers: { 'Accept': 'application/json' },
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new ApiError(errData.error || `Failed to fetch video stream URL: ${res.statusText}`, res.status);
  }
  return res.json();
}

/**
 * Resolves a streaming URL for an authenticated pre-generated highlight clip.
 * Returns direct GCS V4 signed URL with 24-hour expiration when available.
 */
export async function fetchHighlightClipStreamUrl(gameId: string, clipUrlOrFilename: string): Promise<VideoStreamInfo> {
  const cleanName = clipUrlOrFilename.includes('/')
    ? clipUrlOrFilename.substring(clipUrlOrFilename.lastIndexOf('/') + 1).split('?')[0]
    : clipUrlOrFilename.split('?')[0];
  const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/highlights/${encodeURIComponent(cleanName)}?redirect=false`;
  const res = await authenticatedFetch(url, {
    headers: { 'Accept': 'application/json' },
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new ApiError(errData.error || `Failed to fetch highlight stream URL: ${res.statusText}`, res.status);
  }
  return res.json();
}

/**
 * Resolves storyboard metadata manifest for a game video feed.
 * Returns tile dimensions, interval, sheet count, and format.
 */
export async function fetchStoryboardMetadata(
  gameId: string,
  feed: 'auto' | 'pano' = 'auto'
): Promise<StoryboardMetadata | null> {
  try {
    const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/storyboard?feed=${encodeURIComponent(feed)}`;
    const res = await authenticatedFetch(url, {
      headers: { 'Accept': 'application/json' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data && data.hasStoryboard ? (data as StoryboardMetadata) : null;
  } catch {
    return null;
  }
}

/**
 * Constructs the URL to fetch or stream a specific storyboard sprite sheet image.
 */
export function getStoryboardSheetUrl(
  gameId: string,
  feed: 'auto' | 'pano',
  sheetIndex: number,
  token?: string | null
): string {
  const base = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/storyboard?feed=${encodeURIComponent(feed)}&sheet=${sheetIndex}`;
  return token ? `${base}&token=${encodeURIComponent(token)}` : base;
}

/**
 * Admin action to trigger schedule synchronization and queue unqueued LiveBarn downloads.
 */
export async function syncScheduleVideos(): Promise<{ status: string; queuedGamesCount: number; queuedGameIds: string[] }> {
    const url = `${getBackendUrl()}/api/admin/videos/sync-schedule`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to sync video schedule: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Admin endpoint: Fetches full video ingestion pipeline status across calendar, tournaments, and Firestore.
 */
export async function fetchIngestionStatus(seasonKey?: string): Promise<IngestionStatusResponse> {
    const params = new URLSearchParams();
    if (seasonKey) params.set('seasonKey', seasonKey);
    const query = params.toString() ? `?${params.toString()}` : '';
    const url = `${getBackendUrl()}/api/admin/videos/ingestion-status${query}`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to fetch ingestion status: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Admin endpoint: Manually schedules or reschedules a game video download.
 */
export async function scheduleGameDownload(gameId: string, payload?: { scheduledFor?: string; opponent?: string; rinkName?: string; surfaceId?: number }): Promise<any> {
    const url = `${getBackendUrl()}/api/admin/videos/schedule-download`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gameId, ...(payload || {}) }),
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to schedule game download: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Admin endpoint: Retries a specific feed (e.g. panoramic feed).
 */
export async function retryGameDownload(gameId: string, feedType: string = 'pano'): Promise<any> {
    const url = `${getBackendUrl()}/api/admin/videos/retry-download`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gameId, feedType }),
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to retry download: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Adds a manual game event or goal marker (Admin or Manager).
 */
export async function addGameEvent(gameId: string, eventData: {
    type?: string;
    scoringTeam?: string;
    scoreAfter?: string;
    period?: number;
    gameClock?: string;
    videoTimestamp: number;
    videoTimestampStr?: string;
    description?: string;
    clipStartSeconds?: number;
    clipEndSeconds?: number;
    durationSeconds?: number;
    scorer?: string;
    scorerJersey?: string;
    assists?: string[];
}): Promise<any> {
    const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/events`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eventData),
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to add game event: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Deletes a manual game event and companion goal marker (Admin or Manager).
 */
export async function deleteGameEvent(gameId: string, eventId: string): Promise<any> {
    const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/events/${encodeURIComponent(eventId)}`;
    const res = await authenticatedFetch(url, {
        method: 'DELETE',
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to delete game event: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Updates the period regions (start and end times) for a game (Admin).
 */
export async function updateGamePeriods(gameId: string, periods: GamePeriod[]): Promise<{ status: string; periods: GamePeriod[] }> {
    const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/periods`;
    const res = await authenticatedFetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ periods }),
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to update periods: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Updates a goal marker's timestamp, period, scorer, or clock (Admin).
 */
export async function updateGameGoal(gameId: string, goalId: string | number, updates: Partial<GoalEventData>): Promise<{ status: string; goal: GoalEventData }> {
    const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/goals/${encodeURIComponent(String(goalId))}`;
    const res = await authenticatedFetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to update goal: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Fetches all timeline events (notes & clips) that the current user has permission to see.
 */
export async function fetchTimelineEvents(gameId: string): Promise<TimelineEventItem[]> {
    const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/timeline-events`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to fetch timeline events: ${res.statusText}`, res.status);
    }
    const data = await res.json();
    return (data.events || []).map((ev: any) => ({
        ...ev,
        eventType: (ev.eventType || ev.type || 'note') as 'note' | 'clip',
    }));
}

/**
 * Creates a new timeline event (Note or Clip).
 */
export async function createTimelineEvent(gameId: string, eventData: Partial<TimelineEventItem>): Promise<TimelineEventItem> {
    const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/timeline-events`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eventData),
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to create timeline event: ${res.statusText}`, res.status);
    }
    const data = await res.json();
    return {
        ...data.event,
        eventType: (data.event.eventType || data.event.type || eventData.eventType || 'note') as 'note' | 'clip',
    };
}

/**
 * Updates an existing timeline event (Note or Clip).
 */
export async function updateTimelineEvent(gameId: string, eventId: string, updates: Partial<TimelineEventItem>): Promise<TimelineEventItem> {
    const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/timeline-events/${encodeURIComponent(eventId)}`;
    const res = await authenticatedFetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to update timeline event: ${res.statusText}`, res.status);
    }
    const data = await res.json();
    return data.event;
}

/**
 * Deletes a timeline event (Note or Clip).
 */
export async function deleteTimelineEvent(gameId: string, eventId: string): Promise<void> {
    const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/timeline-events/${encodeURIComponent(eventId)}`;
    const res = await authenticatedFetch(url, {
        method: 'DELETE',
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to delete timeline event: ${res.statusText}`, res.status);
    }
}

/**
 * Updates sharing permissions for a timeline event.
 */
export async function updateTimelineEventShares(
    gameId: string,
    eventId: string,
    shareData: {
        generalAccess?: GeneralAccessLevel;
        publicRole?: ShareAccessRole;
        shares?: TimelineShareTarget[];
    }
): Promise<TimelineEventItem> {
    const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/timeline-events/${encodeURIComponent(eventId)}/shares`;
    const res = await authenticatedFetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(shareData),
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to update timeline event shares: ${res.statusText}`, res.status);
    }
    const data = await res.json();
    return data.event;
}

/**
 * Fetches pre-extracted audio waveform data for a game.
 */
export async function fetchAudioWaveform(gameId: string): Promise<AudioWaveformSummary | null> {
    const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/waveform`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        return null;
    }
    return res.json();
}

/**
 * Fetches timeline edit history for a game.
 */
export async function fetchGameEditHistory(gameId: string): Promise<TimelineAuditEntry[]> {
    const url = `${getBackendUrl()}/api/videos/${encodeURIComponent(gameId)}/history`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        return [];
    }
    const data = await res.json();
    return data.history || [];
}

/**
 * Admin endpoint: Reconciles Firestore video records against actual GCS files.
 */
export async function reconcileGameVideos(gameId?: string): Promise<any> {
    const url = `${getBackendUrl()}/api/admin/videos/reconcile`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(gameId ? { gameId } : {}),
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to reconcile videos: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Admin endpoint: Fetches full task debug execution logs from GCS and Firestore telemetry.
 */
export async function fetchGameTaskLogs(gameId: string, taskId?: string): Promise<GameTaskLogResponse> {
    const query = taskId ? `?taskId=${encodeURIComponent(taskId)}` : '';
    const url = `${getBackendUrl()}/api/admin/videos/${encodeURIComponent(gameId)}/logs${query}`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to fetch game task logs: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Admin endpoint: Repairs an existing video file by placing the moov atom at the front (+faststart).
 */
export async function repairVideoFaststart(gameId: string, feed: string = 'pano'): Promise<FaststartRepairResponse> {
    const url = `${getBackendUrl()}/api/admin/videos/${encodeURIComponent(gameId)}/faststart-repair`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ feed }),
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to repair video faststart: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Fetches notification preferences for the logged-in user.
 */
export async function fetchUserPreferences(): Promise<UserNotificationPreferences> {
    const url = `${getBackendUrl()}/api/user/preferences`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        throw new ApiError(`Failed to fetch notification preferences: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Updates notification preferences for the logged-in user.
 */
export async function updateUserPreferences(preferences: Partial<UserNotificationPreferences>): Promise<{ status: string; preferences: UserNotificationPreferences }> {
    const url = `${getBackendUrl()}/api/user/preferences`;
    const res = await authenticatedFetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences),
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to update notification preferences: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Sends an immediate preview sample email to the logged-in user.
 */
export async function sendNotificationPreview(type: 'daily' | 'weekly'): Promise<{ status: string; message: string; details?: any }> {
    const url = `${getBackendUrl()}/api/user/preferences/preview`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type }),
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to send notification preview: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Admin endpoint: Fetches recent email dispatch runs, delivery errors, and subscriber metrics.
 */
export async function fetchEmailDispatches(limit: number = 20): Promise<EmailDispatchesResponse> {
    const url = `${getBackendUrl()}/api/admin/email/dispatches?limit=${encodeURIComponent(limit)}`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to fetch email dispatches: ${res.statusText}`, res.status);
    }
    const data = await res.json();
    if (Array.isArray(data)) {
        return { dispatches: data, dailySubscribers: 0, weeklySubscribers: 0 };
    }
    return {
        dispatches: data.dispatches || [],
        dailySubscribers: data.dailySubscribers || 0,
        weeklySubscribers: data.weeklySubscribers || 0,
    };
}

/**
 * Admin endpoint: Manually triggers daily or weekly email dispatch runs.
 */
export async function triggerAdminDispatch(jobType: 'daily' | 'weekly', force: boolean = false): Promise<{ status: string; result: any }> {
    const url = `${getBackendUrl()}/api/admin/email/trigger-dispatch`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobType, force }),
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to trigger email dispatch: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Fetches unified calendar events (Crossbar iCal + rollover + tournaments + duties + holidays + birthdays) from the backend.
 */
export async function fetchUnifiedCalendarEvents(options?: {
    seasonKey?: string;
    tags?: string;
    keepPlaceholders?: boolean;
    includeBirthdays?: boolean;
    includeHolidays?: boolean;
    forceRefresh?: boolean;
}): Promise<CalendarEvent[]> {
    const params = new URLSearchParams();
    if (options?.seasonKey) params.set('seasonKey', options.seasonKey);
    if (options?.tags) params.set('tags', options.tags);
    if (options?.keepPlaceholders !== undefined) params.set('keepPlaceholders', String(options.keepPlaceholders));
    if (options?.includeBirthdays !== undefined) params.set('includeBirthdays', String(options.includeBirthdays));
    if (options?.includeHolidays !== undefined) params.set('includeHolidays', String(options.includeHolidays));
    if (options?.forceRefresh) params.set('refresh', 'true');

    const qs = params.toString();
    const url = `${getBackendUrl()}/api/calendar/unified${qs ? `?${qs}` : ''}`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
        throw new ApiError(`Failed to fetch unified calendar events: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Fetches unified standings, scored games (including unofficial finals), and full schedule from the backend.
 */
export async function fetchUnifiedStandings(
    seasonId: string,
    divisionId: string,
    forceRefresh: boolean = false
): Promise<UnifiedStandingsResponse> {
    if (
        typeof process !== 'undefined' &&
        process.env?.NODE_ENV === 'test' &&
        !(global.fetch as any)?._isMockFunction
    ) {
        throw new Error('Skipping unmocked unified standings fetch in test environment');
    }
    const params = new URLSearchParams({ seasonId, divisionId });
    if (forceRefresh) params.set('refresh', 'true');
    const url = `${getBackendUrl()}/api/standings?${params.toString()}`;
    const token = await getAuthToken();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(url, { headers });
    if (!res.ok) {
        throw new ApiError(`Failed to fetch unified standings: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Fetches unified last-week and upcoming-week matchups along with pre-window KRACH predictions in one round-trip.
 */
export async function fetchWeeklyMatchups(
    seasonId: string,
    divisionId: string,
    forceRefresh: boolean = false
): Promise<WeeklyMatchupsResponse> {
    if (
        typeof process !== 'undefined' &&
        process.env?.NODE_ENV === 'test' &&
        !(global.fetch as any)?._isMockFunction
    ) {
        throw new Error('Skipping unmocked weekly matchups fetch in test environment');
    }
    const params = new URLSearchParams({ seasonId, divisionId });
    if (forceRefresh) params.set('refresh', 'true');
    const url = `${getBackendUrl()}/api/weekly-matchups?${params.toString()}`;
    const token = await getAuthToken();
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(url, { headers });
    if (!res.ok) {
        throw new ApiError(`Failed to fetch weekly matchups: ${res.statusText}`, res.status);
    }
    return res.json();
}

/**
 * Rotates (resets) the logged-in user's personal .ics calendar subscription token, revoking any previous feed URL.
 */
export async function resetCalendarFeedToken(): Promise<{
    status: string;
    calendarFeedToken: string;
    preferences: UserNotificationPreferences;
}> {
    const url = `${getBackendUrl()}/api/user/preferences/calendar-token/reset`;
    const res = await authenticatedFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new ApiError(errData.error || `Failed to reset calendar feed token: ${res.statusText}`, res.status);
    }
    return res.json();
}


